// Recrutement v2 — période d'essai, dernière étape du recrutement.
// Ligne des points de contrôle (J+15 → décision), formulaire par type
// (alerte, revue sur la grille d'embauche, pouls équipe, décision finale
// co-signée manager + RH + owner), objectifs des 90 premiers jours.
import { useState } from 'react';
import { REC_DIMS, REC_PULSE_QUESTIONS, recDimScore, type RecGridCrit } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useMe, useRecObCheckpoint, useRecObOutcome, type RecCheckpointDTO, type RecOnboardingDTO } from '../../api/hooks.js';
import { GradeGroups, V2Ic, fmt1 } from './V2Ui.js';

const errMsg = (fallback: string) => (err: unknown) =>
  toast.danger((err as { body?: { message?: string } }).body?.message ?? fallback);
const fmtD = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');
const ICON: Record<string, string> = { alert: 'bell', review: 'star', pulse: 'team', final: 'gavel' };
const OST: Array<[string, string]> = [['OK', 'Atteint'], ['ON', 'En bonne voie'], ['LATE', 'En retard']];

export function obDay(ob: Pick<RecOnboardingDTO, 'start'>): number {
  return Math.round((Date.now() - new Date(`${ob.start}T00:00:00`).getTime()) / 864e5);
}
export function cpState(cp: RecCheckpointDTO, day: number): { k: 'done' | 'late' | 'due' | 'todo'; lbl: string } {
  if (cp.done) return { k: 'done', lbl: 'Fait' };
  if (day > cp.day) return { k: 'late', lbl: `En retard · ${day - cp.day} j` };
  if (day >= cp.day - 2) return { k: 'due', lbl: day === cp.day ? 'Aujourd’hui' : `Dans ${cp.day - day} j` };
  return { k: 'todo', lbl: day < 0 ? fmtD(cp.dueDate).slice(0, 5) : `J−${cp.day - day}` };
}

/** Bandeau compact pour la carte de cohorte. */
export function ProbationStrip({ ob }: { ob: RecOnboardingDTO }) {
  if (!ob.checkpoints.length) return null;
  const day = obDay(ob);
  return (
    <div>
      <div className="rv2-pbtrack">
        {ob.checkpoints.map((c, j) => {
          const k = cpState(c, day).k;
          return (
            <span key={c.key} style={{ display: 'contents' }}>
              {j > 0 && <span className={`rv2-pbl${ob.checkpoints[j - 1]!.done ? ' done' : ''}`}></span>}
              <span className={`rv2-pbn ${k}`} title={`${c.lbl} — ${cpState(c, day).lbl}`}>{c.type === 'final' ? '✦' : c.day}</span>
            </span>
          );
        })}
      </div>
      <div className="rv2-pblab"><span>essai {ob.probationMonths} mois{ob.probationRenewable ? ' · renouvelable' : ''}</span><span>{ob.checkpoints.filter((c) => c.done).length}/{ob.checkpoints.length} points</span></div>
    </div>
  );
}

export function ProbationSection({ ob, canRh }: { ob: RecOnboardingDTO; canRh: boolean }) {
  const me = useMe();
  const outcome = useRecObOutcome();
  const day = obDay(ob);
  const end = ob.probationMonths * 30;
  const firstOpen = ob.checkpoints.find((c) => !c.done) ?? ob.checkpoints[ob.checkpoints.length - 1];
  const [sel, setSel] = useState(firstOpen?.key ?? '');
  const meId = me.data?.user.id;
  const isBoss = me.data?.user.role === 'BOSS';
  const isMgr = meId === ob.hiringManagerId || isBoss;
  const canCp = isMgr && (me.data?.permissions ?? []).includes('recrutement.edit');
  if (!ob.checkpoints.length) {
    return <div className="rec-emptybox big">Pas de points de contrôle sur cet onboarding (créé avant la v2).</div>;
  }
  const cp = ob.checkpoints.find((c) => c.key === sel) ?? firstOpen!;
  const pct = (d: number) => Math.min(100, Math.max(0, (d / end) * 100));
  const hire = recDimScore(ob.grid, ob.hireScores);
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="ob9-journey" style={{ marginBottom: 0 }}>
        <div className="ob9-jh">
          <h4>Période d’essai — points de contrôle</h4>
          <span className="k">essai {ob.probationMonths} mois{ob.probationRenewable ? ' · renouvelable' : ''} · décision écrite avant le {fmtD(new Date(new Date(`${ob.start}T12:00:00`).getTime() + end * 864e5).toISOString())}</span>
          <span className="ob9-today">aujourd’hui <b>{day < 0 ? `J−${-day}` : `J+${day}`}</b></span>
        </div>
        <div className="rv2-cpline">
          <div className="fill" style={{ width: `${pct(day)}%` }}></div>
          {day >= 0 && <div className="mark" style={{ left: `${pct(day)}%` }}><b>aujourd’hui</b></div>}
          {ob.checkpoints.map((c) => {
            const k = cpState(c, day);
            return (
              <button key={c.key} className={`rv2-cpnode ${k.k}${cp.key === c.key ? ' sel' : ''}`} style={{ left: `${pct(c.day)}%` }} onClick={() => setSel(c.key)}>
                <span className="dot">{c.done ? <V2Ic k="check" s={15} w={2.6} /> : <V2Ic k={ICON[c.type]!} s={15} w={2} />}</span>
                <span className="lb">{c.lbl}</span>
                <span className="dt">J+{c.day} · {fmtD(c.dueDate).slice(0, 5)}</span>
                <span className="st">{k.lbl}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Une revue re-note toute la grille à côté du comparatif : pleine largeur. */}
      <div className="r7-grid" style={{ maxWidth: 'none', ...(cp.type === 'review' ? { gridTemplateColumns: 'minmax(0,1fr)' } : {}) }}>
        <div className="r7-col">
          <CpForm ob={ob} cp={cp} day={day} canCp={canCp} canRh={canRh} isMgr={isMgr} isBoss={isBoss} meId={meId ?? ''} />
        </div>
        <div className="r7-col">
          <div className="r7-sec">
            <h4>🎯 Objectifs des 90 premiers jours</h4>
            <p className="cat-note" style={{ margin: '0 0 10px' }}>Copiés de la demande à l’acceptation de l’offre. Statut mis à jour à chaque point.</p>
            {ob.outcomes.map((o, i) => (
              <div key={i} className="rv2-vrow">
                <div className="nm">{i + 1}. {o.t}</div>
                <span className="rv2-ost">
                  {OST.map(([k, l]) => (
                    <button key={k} disabled={!canCp || outcome.isPending} className={`${k}${o.st === k ? ' on' : ''}`}
                      onClick={() => outcome.mutate({ id: ob.id, index: i, st: k }, { onError: errMsg('Statut non enregistré.') })}>{l}</button>
                  ))}
                </span>
              </div>
            ))}
            {!ob.outcomes.length && <div className="rec-hint">Aucun objectif sur la demande d’origine.</div>}
          </div>
          <div className="r7-sec">
            <h4>🧭 Score d’embauche (moyenne du jury)</h4>
            <div className="rv2-hire4">
              {REC_DIMS.map((d) => (
                <div key={d.id} className="r7-fact" style={{ borderColor: `color-mix(in srgb,${d.c} 28%,var(--line))` }}>
                  <div className="k" style={{ color: d.c }}>{d.lbl}</div>
                  <div className="v" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmt1(hire.dims[d.id])}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="r7-sec">
            <h4>📜 Historique des points</h4>
            <div className="rec-feed">
              {ob.checkpoints.filter((c) => c.done).map((c) => (
                <div key={c.key} className="rec-feedrow"><span className="t">J+{c.day}</span><span className="ic">✓</span><p><b>{c.lbl}</b> — {cpSummary(c)}{c.doneBy ? ` · ${c.doneBy}` : ''}</p></div>
              ))}
              {!ob.checkpoints.some((c) => c.done) && <div className="rec-hint">Aucun point enregistré.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function cpSummary(c: RecCheckpointDTO): string {
  const d = c.data;
  if (c.type === 'alert') return d.v === 'WARN' ? `Avertissement — « ${d.change} »` : 'Sur la bonne voie';
  if (c.type === 'review') return `${({ GO: 'Sur la voie / continuer', RISK: 'À risque', PLAN: 'Plan d’amélioration', STOP: 'Arrêt' } as Record<string, string>)[d.v ?? ''] ?? '—'} · réembaucherait : ${(d.rehire ?? '—').toLowerCase()}${d.drops?.length ? ` · baisse : ${d.drops.join(', ').toLowerCase()}` : ''}`;
  if (c.type === 'pulse') return `${d.answers?.length ?? 0} réponses de l’équipe`;
  return d.decision === 'CONFIRM' ? 'Essai confirmé' : 'Fin d’essai';
}

function CpForm({ ob, cp, day, canCp, canRh, isMgr, isBoss, meId }: {
  ob: RecOnboardingDTO; cp: RecCheckpointDTO; day: number; canCp: boolean; canRh: boolean; isMgr: boolean; isBoss: boolean; meId: string;
}) {
  const save = useRecObCheckpoint();
  const st = cpState(cp, day);
  const [alert, setAlert] = useState<{ v: string | null; change: string }>({ v: cp.data.v ?? null, change: cp.data.change ?? '' });
  const [rv, setRv] = useState<{ scores: Record<string, number>; rehire: string | null; v: string | null; plan: Array<{ t: string; d: string }> }>({
    scores: cp.data.scores ?? {}, rehire: cp.data.rehire ?? null, v: cp.data.v ?? null, plan: cp.data.plan?.length ? cp.data.plan : [{ t: '', d: '' }, { t: '', d: '' }],
  });
  const [pulse, setPulse] = useState<Array<{ who: string; a: string[] }>>(cp.data.answers ?? [{ who: '', a: ['', '', ''] }, { who: '', a: ['', '', ''] }]);
  // Un brouillon par case : une même personne peut avoir le droit de
  // plusieurs cases (owner manager recruteur), mais n'en signe qu'une.
  const [signDraft, setSignDraft] = useState<Record<string, { v: string | null; comment: string }>>({});
  const ro = cp.done || (cp.type !== 'final' && !canCp);
  const post = (body: Record<string, unknown>, msg: string) =>
    save.mutate({ id: `${ob.id}/checkpoints/${cp.key}`, ...body }, { onSuccess: () => toast.success(msg), onError: errMsg('Point non enregistré.') });
  const head = (
    <div className="rv2-gatehead" style={{ '--gc': st.k === 'late' ? 'var(--bad)' : st.k === 'done' ? 'var(--green)' : 'var(--shf)' } as React.CSSProperties}>
      <span className="lk" style={{ width: 36, height: 36, borderRadius: 12, display: 'grid', placeItems: 'center', color: '#fff', background: st.k === 'late' ? 'var(--bad)' : st.k === 'done' ? 'var(--green)' : 'var(--shf)' }}><V2Ic k={ICON[cp.type]!} s={17} w={2} /></span>
      <div>
        <b>{cp.lbl} · J+{cp.day} ({fmtD(cp.dueDate)})</b>
        <span>{({ alert: 'Manager — deux semaines : est-ce que ça démarre comme prévu ?', review: 'Manager — même grille qu’à l’embauche, comparaison dimension par dimension', pulse: 'Manager — 2 à 3 collègues, 3 questions', final: 'Manager + RH + owner — décision écrite, les trois avis doivent concorder' } as Record<string, string>)[cp.type]}</span>
      </div>
      <span className={`rec-stpill ${st.k === 'late' ? 'bad' : st.k === 'due' ? 'warn' : st.k === 'done' ? 'green' : 'mute'}`} style={{ marginLeft: 'auto' }}>{st.lbl}</span>
    </div>
  );
  const roNote = !cp.done && cp.type !== 'final' && !canCp
    ? <div className="rv2-ovwarn" style={{ marginBottom: 12, background: 'var(--infos)', color: 'var(--infoc)' }}>Ce point est rempli par le manager recruteur ({ob.manager ?? '—'}) — lecture seule.</div>
    : null;

  if (cp.type === 'alert') {
    const ok = alert.v === 'OK' || (alert.v === 'WARN' && alert.change.trim().length > 0);
    return (
      <div className="r7-sec">
        {head}{roNote}
        <div className="rec-verdict" style={{ marginTop: 0 }}>
          <button className={`go${alert.v === 'OK' ? ' on' : ''}`} disabled={ro} onClick={() => setAlert({ ...alert, v: 'OK' })}>Sur la bonne voie</button>
          <button className={`mid${alert.v === 'WARN' ? ' on' : ''}`} disabled={ro} onClick={() => setAlert({ ...alert, v: 'WARN' })}>Avertissement</button>
        </div>
        {alert.v === 'WARN' && <div className="rec-field"><label>Ce qui doit changer d’ici la prochaine revue — obligatoire</label><textarea rows={3} disabled={ro} value={alert.change} onChange={(e) => setAlert({ ...alert, change: e.target.value })} placeholder="Concret et vérifiable"></textarea></div>}
        {!ro && <button className="rec-go" style={{ marginTop: 14 }} disabled={!ok || save.isPending} onClick={() => post({ v: alert.v, ...(alert.v === 'WARN' ? { change: alert.change.trim() } : {}) }, 'Alerte précoce enregistrée.')}>Enregistrer le point</button>}
      </div>
    );
  }

  if (cp.type === 'review') {
    const grid = ob.grid as RecGridCrit[];
    const hire = recDimScore(grid, ob.hireScores);
    const now = recDimScore(grid, rv.scores);
    const first = ob.checkpoints.filter((c) => c.type === 'review')[0]?.key === cp.key;
    const opts: Array<[string, string, string]> = first ? [['GO', 'Sur la voie', 'go'], ['RISK', 'À risque', 'mid'], ['STOP', 'Arrêt', 'stop']] : [['GO', 'Continuer', 'go'], ['PLAN', 'Plan d’amélioration', 'mid'], ['STOP', 'Arrêt', 'stop']];
    const plan = rv.plan.filter((p) => p.t.trim().length >= 3 && p.d);
    const ok = now.complete && rv.rehire && rv.v && (rv.v !== 'PLAN' || (plan.length >= 2 && plan.length <= 3));
    const drops = REC_DIMS.filter((d) => now.dims[d.id] != null && hire.dims[d.id] != null && now.dims[d.id]! - hire.dims[d.id]! < -1);
    return (
      <div className="r7-sec">
        {head}{roNote}
        <div className="rv2-rvcols">
          <div>
            <div className="rec-hint" style={{ marginBottom: 8 }}>Le trait sous un bouton marque la note d’embauche.</div>
            <GradeGroups grid={grid} scores={rv.scores} readOnly={ro} refScores={ob.hireScores} showEvidence={false} onScore={(id, v) => setRv({ ...rv, scores: { ...rv.scores, [id]: v } })} />
          </div>
          <div>
            <div style={{ overflowX: 'auto' }}>
              <table className="rv2-cmpt">
                <thead><tr><th>Dimension</th><th></th><th style={{ textAlign: 'right' }}>Embauche</th><th style={{ textAlign: 'right' }}>Maint.</th><th style={{ textAlign: 'right' }}>Écart</th></tr></thead>
                <tbody>
                  {REC_DIMS.map((d) => {
                    const h = hire.dims[d.id], n = now.dims[d.id];
                    const dl = h != null && n != null ? n - h : null;
                    return (
                      <tr key={d.id}>
                        <td><span className="rv2-dim" style={{ '--dc': d.c } as React.CSSProperties}><i></i>{d.short}</span></td>
                        <td><span className="rv2-dbl" style={{ '--dc': d.c } as React.CSSProperties}><i className="h" style={{ width: `${((h ?? 0) / 5) * 100}%` }}></i><i className={`n${dl != null && dl < -1 ? ' ko' : ''}`} style={{ width: `${((n ?? 0) / 5) * 100}%` }}></i></span></td>
                        <td className="num">{fmt1(h)}</td>
                        <td className="num">{fmt1(n)}</td>
                        <td className={`num ${dl != null && dl < -1 ? 'drop' : dl != null && dl > 0 ? 'up' : ''}`}>{dl == null ? '—' : `${dl > 0 ? '+' : ''}${fmt1(dl)}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {drops.length > 0 && <div className="rec-outband">▼ Baisse de plus de 1,0 : <b>{drops.map((d) => `${d.lbl} ${fmt1(hire.dims[d.id])} → ${fmt1(now.dims[d.id])}`).join(' · ')}</b></div>}
            <div className="rec-lbl">Le·la réembaucherais-je aujourd’hui ?</div>
            <div className="rec-verdict" style={{ marginTop: 0 }}>
              {([['OUI', 'Oui', 'go'], ['HESITATION', 'J’hésite', 'mid'], ['NON', 'Non', 'stop']] as const).map(([k, l, c]) => <button key={k} className={`${c}${rv.rehire === k ? ' on' : ''}`} disabled={ro} onClick={() => setRv({ ...rv, rehire: k })}>{l}</button>)}
            </div>
            <div className="rec-lbl">Décision du point</div>
            <div className="rec-verdict" style={{ marginTop: 0 }}>
              {opts.map(([k, l, c]) => <button key={k} className={`${c}${rv.v === k ? ' on' : ''}`} disabled={ro} onClick={() => setRv({ ...rv, v: k })}>{l}</button>)}
            </div>
            {rv.v === 'PLAN' && (
              <div style={{ marginTop: 12 }}>
                <div className="rec-lbl">Plan d’amélioration — 2 à 3 objectifs datés</div>
                {rv.plan.map((x, i) => (
                  <div key={i} className="cated-row">
                    <input disabled={ro} value={x.t} placeholder="Objectif vérifiable" onChange={(e) => { const p = [...rv.plan]; p[i] = { ...p[i]!, t: e.target.value }; setRv({ ...rv, plan: p }); }} />
                    <input disabled={ro} type="date" className="cated-when" value={x.d} onChange={(e) => { const p = [...rv.plan]; p[i] = { ...p[i]!, d: e.target.value }; setRv({ ...rv, plan: p }); }} />
                  </div>
                ))}
                {rv.plan.length < 3 && !ro && <button className="rec-addrow" onClick={() => setRv({ ...rv, plan: [...rv.plan, { t: '', d: '' }] })}>＋ Objectif</button>}
              </div>
            )}
            {rv.v === 'STOP' && <div className="rec-outband">Arrêt : signalé au BOSS. La décision finale se signe ensuite à trois.</div>}
            {!ro && <button className="rec-go" style={{ marginTop: 14, width: '100%' }} disabled={!ok || save.isPending}
              onClick={() => post({ scores: rv.scores, rehire: rv.rehire, v: rv.v, plan: rv.v === 'PLAN' ? plan : [] }, `${cp.lbl} enregistrée.`)}>Enregistrer la revue</button>}
          </div>
        </div>
      </div>
    );
  }

  if (cp.type === 'pulse') {
    const filled = pulse.filter((p) => p.who.trim() && p.a.every((x) => x.trim()));
    return (
      <div className="r7-sec">
        {head}{roNote}
        <div className="r7-facts" style={{ marginTop: 0 }}>
          {pulse.map((p, i) => (
            <div key={i} className="r7-fact" style={{ display: 'grid', gap: 8 }}>
              <input className="rec-ta" disabled={ro} value={p.who} placeholder="Collègue (nom · fonction)" onChange={(e) => { const x = [...pulse]; x[i] = { ...p, who: e.target.value }; setPulse(x); }} />
              {REC_PULSE_QUESTIONS.map((q, j) => (
                <div key={q}>
                  <div className="rec-hint" style={{ fontSize: 10 }}>{q}</div>
                  <textarea className="rec-ta" rows={2} disabled={ro} value={p.a[j] ?? ''} onChange={(e) => { const x = [...pulse]; const a = [...p.a]; a[j] = e.target.value; x[i] = { ...p, a }; setPulse(x); }}></textarea>
                </div>
              ))}
            </div>
          ))}
        </div>
        {!ro && pulse.length < 3 && <button className="rec-addrow" style={{ marginTop: 10 }} onClick={() => setPulse([...pulse, { who: '', a: ['', '', ''] }])}>＋ Un 3ᵉ collègue</button>}
        {!ro && <button className="rec-go" style={{ marginTop: 14 }} disabled={filled.length < 2 || save.isPending} onClick={() => post({ answers: filled }, `Pouls clos — ${filled.length} réponses.`)}>Clore le pouls</button>}
        {!ro && filled.length < 2 && <span className="rec-hint" style={{ marginLeft: 10 }}>2 réponses complètes minimum.</span>}
      </div>
    );
  }

  // décision finale — trois cases
  const signs = cp.data.signs ?? {};
  const mine = Object.entries(signs).find(([, sg]) => sg.byId === meId)?.[0];
  // Owner manager recruteur : une seule signature remplit Manager et
  // Owner (le serveur copie la case) — la RH reste une autre personne.
  const dualMe = isBoss && meId === ob.hiringManagerId;
  const slots: Array<{ k: 'mgr' | 'rh' | 'boss'; who: string; c: string; allowed: boolean }> = [
    { k: 'mgr', who: `Manager · ${ob.manager ?? '—'}${dualMe ? ' · vaut Owner' : ''}`, c: '#2a6fdb', allowed: isMgr && !mine },
    { k: 'rh', who: 'RH', c: '#7037ef', allowed: canRh && !mine },
    { k: 'boss', who: 'Owner', c: '#0c0c0e', allowed: isBoss && !mine && !dualMe },
  ];
  const vs = slots.map((x) => signs[x.k]?.v).filter(Boolean);
  const disagree = vs.length > 1 && new Set(vs).size > 1;
  return (
    <div className="r7-sec">
      {head}
      {cp.done && <div className={`rec-outband`} style={{ background: cp.data.decision === 'CONFIRM' ? 'var(--greens)' : 'var(--bads)', color: cp.data.decision === 'CONFIRM' ? 'var(--greenc)' : 'var(--badc)', borderColor: 'transparent', marginTop: 0, marginBottom: 12 }}><b>{cp.data.decision === 'CONFIRM' ? 'Essai confirmé' : 'Fin d’essai'}</b> — fiche Contrats mise à jour.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
        {slots.map((x) => {
          const sg = signs[x.k];
          const sign = signDraft[x.k] ?? { v: null, comment: '' };
          const setSign = (d: { v: string | null; comment: string }) => setSignDraft({ ...signDraft, [x.k]: d });
          return (
            <div key={x.k} className={`rv2-sign${sg ? ` ${sg.v}` : ''}`}>
              <div className="who"><i style={{ '--pc': x.c } as React.CSSProperties}>{x.k === 'mgr' ? 'M' : x.k === 'rh' ? 'RH' : 'O'}</i><b>{x.who}</b></div>
              {sg ? (
                <div><span className={`rec-stpill ${sg.v === 'CONFIRM' ? 'green' : 'bad'}`}>{sg.v === 'CONFIRM' ? 'Confirmer' : 'Mettre fin'}</span><div className="rec-hint" style={{ marginTop: 6 }}>« {sg.comment} » — {sg.by}, {fmtD(sg.at)}</div>{sg.dual && x.k === 'boss' && <div className="rec-hint" style={{ marginTop: 4 }}>Signée via la case Manager — l’owner est le manager recruteur.</div>}</div>
              ) : x.allowed && !cp.done ? (
                <>
                  <div className="rec-verdict" style={{ marginTop: 0 }}>
                    <button className={`go${sign.v === 'CONFIRM' ? ' on' : ''}`} onClick={() => setSign({ ...sign, v: 'CONFIRM' })}>Confirmer</button>
                    <button className={`stop${sign.v === 'END' ? ' on' : ''}`} onClick={() => setSign({ ...sign, v: 'END' })}>Mettre fin</button>
                  </div>
                  <textarea className="rec-ta" style={{ marginTop: 10 }} rows={2} value={sign.comment} placeholder="Motivation écrite — obligatoire" onChange={(e) => setSign({ ...sign, comment: e.target.value })}></textarea>
                  <button className="rec-go sm" style={{ marginTop: 8 }} disabled={!sign.v || sign.comment.trim().length < 10 || save.isPending} onClick={() => post({ slot: x.k, v: sign.v, comment: sign.comment.trim() }, 'Signature enregistrée.')}>Signer</button>
                </>
              ) : <span className="rec-hint">{x.k === 'boss' && dualMe && !cp.done ? 'Remplie par votre signature Manager.' : 'En attente de signature'}</span>}
            </div>
          );
        })}
      </div>
      {!mine && !cp.done && slots.filter((x) => x.allowed && !signs[x.k]).length > 1 && <div className="rec-hint" style={{ marginTop: 10 }}>Vous avez le droit de plusieurs cases — une seule signature par personne : signez celle qui correspond à votre rôle sur ce recrutement.</div>}
      {disagree && <div className="rv2-ovwarn" style={{ marginTop: 12 }}>Désaccord — les trois avis doivent concorder : à trancher ensemble.</div>}
    </div>
  );
}
